import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import is from '@sindresorhus/is';
import { noop } from 'lodash';
import Maybe from 'true-myth/maybe';

import { useFeedbackDialogStore } from './use-feedback-dialog-store';
import { HardSkillIcon } from '../icons/HardSkillIcon';
import { LanguageSkillIcon } from '../icons/LanguageSkillIcon';
import { SoftSkillIcon } from '../icons/SoftSkillIcon';
import { DropdownWithSearch } from '../job-architecture/components/DropdownWithSearch';

import type { SkillSearchInputType } from './feedback-dialog-store';
import type { SkillShort, SkillType } from '../../../shared/source/data-contract/skill';
import type { SxProps } from '@mui/material/styles';
import type { FunctionComponent, JSXElementConstructor, SyntheticEvent } from 'react';

const popperListItemIconStyles: SxProps = {
    height: '1rem',
    minWidth: '2rem',
    width: '1rem',
};

const skillIcons: Record<SkillType, JSXElementConstructor<{ readonly variant: 'feedback' }>> = {
    hard: HardSkillIcon,
    language: LanguageSkillIcon,
    soft: SoftSkillIcon,
};

const optionsStyle: SxProps = {
    borderRadius: '0.714rem',
};

export const FeedbackSearchAutocomplete: FunctionComponent<{
    readonly currentFeedbackType: SkillSearchInputType;
}> = ({ currentFeedbackType }) => {
    const setFeedbackInformation = useFeedbackDialogStore((state) => state.setFeedbackInformation);
    const skillSearchInput = useFeedbackDialogStore((state) => state.skillSearchInput);
    const searchSkillsToAddPlaceholderLabel = useFeedbackDialogStore(
        (state) => state.searchSkillsToAddPlaceholderLabel,
    );
    const searchSkillsBySearchTerm = useFeedbackDialogStore((state) => state.searchSkillsBySearchTerm);
    const setSkillSearchInput = useFeedbackDialogStore((state) => state.setSkillSearchInput);
    const searchTerm = skillSearchInput[currentFeedbackType].value.unwrapOr('');
    const skills = skillSearchInput[currentFeedbackType].skills;

    const handleChange = (_: SyntheticEvent, newValue: SkillShort | null): void => {
        if (is.object(newValue)) {
            setFeedbackInformation({
                skill: newValue,
            });
            setSkillSearchInput(Maybe.nothing());
            searchSkillsBySearchTerm();
        }
    };

    const onSearchChange = (newInputValue: string): void => {
        setSkillSearchInput(Maybe.just(newInputValue));
        searchSkillsBySearchTerm();
    };

    const onReset = (): void => {
        setSkillSearchInput(Maybe.nothing());
    };

    return (
        <DropdownWithSearch<SkillShort>
            getOptionLabel={(option) => option.title}
            placeholder={searchSkillsToAddPlaceholderLabel}
            renderOption={(props, option) => {
                const Icon = skillIcons[option.skillType];

                return (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <ListItem {...props} sx={optionsStyle}>
                        <ListItemIcon sx={popperListItemIconStyles}>
                            <Icon variant="feedback" />
                        </ListItemIcon>
                        <ListItemText primary={option.title} />
                    </ListItem>
                );
            }}
            searchTerm={searchTerm}
            suggestedEntities={skills.unwrapOr([])}
            onFocus={noop}
            onOptionClick={handleChange}
            onReset={onReset}
            onSearchChange={onSearchChange}
        />
    );
};
